import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Logo } from "../components/Logo";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      navigate("/console");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    ["name", "Your name", "text", "Jane Doe"],
    ["company", "Company", "text", "Grand Palace Hotels"],
    ["email", "Work email", "email", "you@company.com"],
    ["password", "Password", "password", "Minimum 8 characters"],
  ];

  return (
    <div className="min-h-screen bg-[#f9f9f8] flex flex-col">
      <header className="h-16 border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 h-full flex items-center">
          <Link to="/" data-testid="register-logo-link"><Logo /></Link>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm fade-up">
          <h1 className="font-heading font-light text-3xl tracking-tight">Create your console</h1>
          <p className="text-sm text-neutral-500 mt-2">One console per company. Add your team next.</p>
          <form onSubmit={submit} className="mt-10 space-y-5">
            {fields.map(([k, label, type, ph]) => (
              <div key={k}>
                <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">{label}</label>
                {k === "password" ? (
                  <div className="relative mt-1.5">
                    <input data-testid={`register-${k}-input`} type={showPassword ? "text" : "password"} required minLength={8}
                      value={form[k]} onChange={set(k)} placeholder={ph}
                      className="w-full bg-white border border-neutral-300 rounded-md px-3.5 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] focus:border-transparent" />
                    <button type="button" data-testid="register-password-toggle" onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                ) : (
                  <input data-testid={`register-${k}-input`} type={type} required minLength={1}
                    value={form[k]} onChange={set(k)} placeholder={ph}
                    className="mt-1.5 w-full bg-white border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] focus:border-transparent" />
                )}
              </div>
            ))}
            {error && <p data-testid="register-error" className="text-sm text-red-600">{error}</p>}
            <button data-testid="register-submit-button" type="submit" disabled={loading}
              className="w-full bg-[#111] text-white rounded-full py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-60">
              {loading ? "Creating…" : "Create console"}
            </button>
          </form>
          <p className="text-sm text-neutral-500 mt-8">
            Already registered? <Link to="/login" data-testid="register-to-login-link" className="text-[#111] underline underline-offset-4">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
